<script setup lang="ts">
  import type { OrderOptions } from '@/shared/lib';
  import type { OperationLogResponse } from '@/entities/OperationLog';
  import {
    DefaultTable,
    HeaderTableWithButtons,
    NoDocuments,
    SearchNoResults,
    TableHeader,
    TableLoading,
  } from '@/shared/ui';
  import OperationLogsTableRow from './OperationLogsTableRow.vue';

  withDefaults(
    defineProps<{
      logs: OperationLogResponse[];
      orderOptions: OrderOptions[];
      isFilterActive: boolean;
      formatDate: (value: string) => string;
      formatTime: (value: string) => string;
      isFiltered?: boolean;
      loading?: boolean;
      refreshing?: boolean;
      busy?: boolean;
    }>(),
    {
      isFiltered: false,
      loading: false,
      refreshing: false,
      busy: false,
    }
  );

  const emit = defineEmits<{
    (e: 'update:sort', sort: string, order: string): void;
    (e: 'filter-clicked'): void;
  }>();
</script>

<template>
  <DefaultTable class="divide-y divide-gray-02">
    <thead>
      <tr>
        <HeaderTableWithButtons
          id="header-table-with-buttons"
          colspan="8"
          text="Operações"
          :order-options="orderOptions"
          :is-filter-active="isFilterActive"
          @update:sort="(sort, order) => emit('update:sort', sort, order)"
          @filter-clicked="emit('filter-clicked')"
        />
      </tr>
      <tr v-if="logs.length">
        <TableHeader text="Data" class="text-xs/5" />
        <TableHeader text="Hora" class="text-xs/5" />
        <TableHeader text="Colaborador" class="text-xs/5" />
        <TableHeader text="Cargo" class="text-xs/5" />
        <TableHeader text="IP do Computador" class="text-xs/5" />
        <TableHeader text="Tipo de Documento" class="text-xs/5" />
        <TableHeader text="Evento" class="text-xs/5" />
        <TableHeader text="Descrição" class="text-xs/5" />
      </tr>
    </thead>
    <tbody
      class="bg-white-01 divide-y divide-gray-02 transition-opacity"
      :class="{ 'opacity-60': refreshing }"
      :aria-busy="busy || loading || refreshing"
    >
      <tr v-if="loading">
        <td colspan="8" class="py-8">
          <span class="sr-only">Carregando logs de operações...</span>
          <TableLoading :row-size="5" :column-size="8" />
        </td>
      </tr>
      <tr v-else-if="!logs.length">
        <td colspan="8" class="py-8">
          <SearchNoResults v-if="isFiltered" />
          <NoDocuments v-else title="Ops! Parece que ainda não há conteúdo nessa página" />
        </td>
      </tr>
      <template v-else>
        <OperationLogsTableRow
          v-for="log in logs"
          :key="log.id"
          :log="log"
          :format-date="formatDate"
          :format-time="formatTime"
        />
      </template>
    </tbody>
  </DefaultTable>
</template>
